import { Auth0ContextInterface } from "@auth0/auth0-react";
import axios, { AxiosError } from "axios";
import Cookies from "js-cookie";
import { UserContext } from "../context";
import { config as appConfig } from "../config";
import { pickLatestExpiringToken } from "./sessionManager";

/**
 * Generate MAS token from Auth0 token
 */
export async function generateMASToken(auth0Token: string): Promise<string> {
    const url = `${appConfig.SERVICES_API_URL}/ums-v1/mas/tokenAuth`;

    const config = {
        headers: { Authorization: `Bearer ${auth0Token}` },
    };

    try {
        const { data } = await axios.post(url, {}, config);
        if (!data || !data.token) {
            throw new Error("MAS did not return a token");
        }
        return data.token as string;
    } catch (error) {
        const status = (error as AxiosError).response?.status;
        if (status === 401) {
            const err = new Error("TOKEN_AUTH_FAILED");
            (err as any).code = "TOKEN_AUTH_FAILED";
            throw err;
        }
        throw error;
    }
}

/**
 * Perform Auth0 login and get MAS token
 */
export async function performAuth0Login(
    auth0: Auth0ContextInterface,
    context: UserContext
): Promise<string> {
    const { getAccessTokenSilently } = auth0;

    try {
        const auth0Token = await getAccessTokenSilently({
            authorizationParams: {
                scope: "openid profile email",
            },
        });

        const masToken = await generateMASToken(auth0Token);
        return masToken;

    } catch (e: any) {
        console.error("Error in Auth0 login", e);
        throw e;
    }
}

/**
 * Returns the freshest MAS token available to this tab: the later-expiring of
 * the in-memory token and the shared cookie. The cookie is the cross-tab
 * transport, so a follower tab whose in-memory token is stale still sees the
 * leader's refreshed token here.
 */
export function getActiveSessionToken(currentMasToken?: string): string | undefined {
    return pickLatestExpiringToken(
        [currentMasToken, Cookies.get("mercury_shared_token")],
        getTokenExpiryMs
    );
}

/**
 * Milliseconds until the freshest available MAS token expires. Negative once
 * expired, null when there is no decodable token.
 */
export function getSessionRemainingMs(currentMasToken?: string): number | null {
    const expiry = getTokenExpiryMs(getActiveSessionToken(currentMasToken));
    return expiry === null ? null : expiry - Date.now();
}

/**
 * Extend active session by requesting a fresh MAS token from backend.
 * UMS only refreshes tokens that are still valid, so callers should stop
 * offering this once the token has expired.
 */
export async function extendSessionToken(
    currentMasToken?: string
): Promise<string> {
    const activeMasToken = getActiveSessionToken(currentMasToken);

    if (!activeMasToken) {
        const err = new Error("No MAS token available for session refresh");
        (err as any).code = "NO_MAS_TOKEN";
        throw err;
    }

    // Refresh MAS tokens without calling Auth0 again.
    const url = `${appConfig.SERVICES_API_URL}/ums-v1/mas/refresh`;
    const requestConfig = {
        headers: {
            Authorization: `Bearer ${activeMasToken}`,
        },
    };

    try {
        const { data } = await axios.post(url, {}, requestConfig);
        if (!data?.token) {
            throw new Error("MAS refresh did not return a token");
        }

        console.log("[extendSessionToken] New MAS token received from UMS");
        return data.token as string;
    } catch (error: any) {
        const status = (error as AxiosError).response?.status;
        if (status === 401) {
            const err = new Error("MAS_TOKEN_REFRESH_FAILED");
            (err as any).code = "MAS_TOKEN_REFRESH_FAILED";
            throw err;
        }

        console.error("[extendSessionToken] MAS token refresh failed:", error?.message || error);
        throw error;
    }
}

/**
 * Perform Auth0 logout by redirecting to the /v2/logout endpoint directly.
 *
 * The Auth0 SDK cannot be used here: Auth0Provider is not mounted while a
 * valid MAS token is in use, so useAuth0() returns a stub whose logout()
 * throws. Building the URL ourselves works in both cases, and the full-page
 * redirect discards any in-memory SDK state anyway.
 */
export function performAuth0Logout(returnToPath?: string): void {
    if (!appConfig.AUTH_DOMAIN || !appConfig.AUTH_CLIENT_ID) {
        throw new Error("Auth0 domain/client id missing, cannot build logout URL");
    }

    const domain = appConfig.AUTH_DOMAIN.replace(/^https?:\/\//, "");
    const returnTo = returnToPath || `${window.location.origin}/logout`;

    const params = new URLSearchParams({
        client_id: appConfig.AUTH_CLIENT_ID,
        returnTo,
    });

    // federated also ends the upstream identity-provider session; without it
    // the IdP silently re-authenticates on the next login redirect.
    window.location.href = `https://${domain}/v2/logout?${params.toString()}&federated`;
}

/**
 * Safely verify JWT (MAS token)
 */
export function verifyToken(token?: string): boolean {
    const expiry = getTokenExpiryMs(token);
    return expiry !== null && Date.now() < expiry;
}

/**
 * Returns JWT expiry in milliseconds, or null for invalid tokens.
 */
export function getTokenExpiryMs(token?: string): number | null {
    if (!token) return null;

    try {
         // Extract JWT payload
        const parts = token.split(".");
        if (parts.length !== 3) return null;

        // Convert to standard base64
        const base64 = parts[1]
            .replace(/-/g, "+")
            .replace(/_/g, "/");

        // Decode and parse JSON payload
        const payload = JSON.parse(
            decodeURIComponent(
                window.atob(base64)
                    .split("")
                    .map(c => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
                    .join("")
            )
        );

        if (!payload?.exp || typeof payload.exp !== "number") {
            return null;
        }

        return payload.exp * 1000;
    } catch {
        return null;
    }
}
